/**
 * DAG-CBOR encoder subset (encode-only, byte-identical across runtimes — F4, ADR-12).
 *
 * Types: null, bool, integers (major 0/1), UTF-8 text (major 3), bytes (major 2),
 * arrays (major 4), maps (major 5, text keys only), explicit floats (major 7 /
 * canonical float64, via the CborFloat box — F7). Shortest-form heads. Map keys
 * sorted length-first then bytewise on the UTF-8 bytes (IPLD DAG-CBOR / RFC 7049 §3.9).
 * Non-finite floats, bare fractional numbers, tags, undefined, non-plain objects →
 * error (determinism by construction; floats only via CborFloat — never sniffed).
 */
import { concatBytes, utf8 } from "./bytes.js";

/** Explicit float box — the encoder emits major-7 float64 only for this type;
 * a bare `number` must still be a safe integer (ADR-12 "no guessing"). */
export class CborFloat {
  constructor(public readonly value: number) {}
}

export type CborValue =
  | null
  | boolean
  | number
  | string
  | Uint8Array
  | CborValue[]
  | CborFloat
  | { [k: string]: CborValue };

function head(major: number, n: number | bigint): Uint8Array {
  const v = BigInt(n);
  if (v < 0n) throw new Error("dag-cbor: negative head argument");
  const m = major << 5;
  if (v < 24n) return new Uint8Array([m | Number(v)]);
  if (v <= 0xffn) return new Uint8Array([m | 24, Number(v)]);
  if (v <= 0xffffn) return new Uint8Array([m | 25, Number(v >> 8n), Number(v & 0xffn)]);
  if (v <= 0xffffffffn) {
    return new Uint8Array([
      m | 26,
      Number((v >> 24n) & 0xffn),
      Number((v >> 16n) & 0xffn),
      Number((v >> 8n) & 0xffn),
      Number(v & 0xffn),
    ]);
  }
  const out = new Uint8Array(9);
  out[0] = m | 27;
  let x = v;
  for (let i = 8; i >= 1; i--) {
    out[i] = Number(x & 0xffn);
    x >>= 8n;
  }
  return out;
}

/** Bytewise lexicographic compare after length (length-first map key order). */
function compareKeys(a: Uint8Array, b: Uint8Array): number {
  if (a.length !== b.length) return a.length - b.length;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

export function dagCborEncode(v: CborValue): Uint8Array {
  if (v === null) return new Uint8Array([0xf6]);
  if (typeof v === "boolean") return new Uint8Array([v ? 0xf5 : 0xf4]);
  if (typeof v === "number") {
    if (!Number.isSafeInteger(v)) throw new Error("dag-cbor: floats/unsafe ints forbidden");
    return v >= 0 ? head(0, v) : head(1, -1 - v);
  }
  if (typeof v === "string") {
    const b = utf8(v);
    return concatBytes(head(3, b.length), b);
  }
  if (v instanceof Uint8Array) return concatBytes(head(2, v.length), v);
  if (Array.isArray(v)) return concatBytes(head(4, v.length), ...v.map(dagCborEncode));
  if (v instanceof CborFloat) {
    const n = v.value;
    if (!Number.isFinite(n)) throw new Error("dag-cbor: non-finite float forbidden");
    const norm = n === 0 ? 0 : n; // -0.0 → +0.0
    const buf = new Uint8Array(9);
    buf[0] = 0xfb;
    new DataView(buf.buffer).setFloat64(1, norm, false); // big-endian
    return buf;
  }
  if (typeof v === "object") {
    const proto = Object.getPrototypeOf(v);
    if (proto !== Object.prototype && proto !== null) {
      throw new Error("dag-cbor: only plain objects may be maps");
    }
    const entries = Object.entries(v).map(([k, val]) => ({ key: utf8(k), val }));
    entries.sort((a, b) => compareKeys(a.key, b.key));
    return concatBytes(
      head(5, entries.length),
      ...entries.flatMap((e) => [concatBytes(head(3, e.key.length), e.key), dagCborEncode(e.val)]),
    );
  }
  throw new Error(`dag-cbor: unsupported value ${typeof v}`);
}
